import { FLOOR_FALL_ARRIVAL_MS, FLOOR_FALL_MS, floorFallOffsetAt } from "game-common/floor_fall"
import { leapFallTrajectory } from "game-common/jump"
import { BODY_GROUND_OFFSET } from "game-common/lpc/body_placement"
import { Entity, PredictedFall, TileSize } from "game-common/models"
import { fallLandingNear } from "game-common/room/ember/fall_landing"
import { STOREY_RISE_PX } from "game-common/room/ember/two_storey_house"
import { wallTopOpenAt } from "game-common/room/ember/wall_top"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"

/**
 * THIS CLIENT'S OWN PLAYER GOING OVER AN EDGE, DROPPED HERE AT ONCE.
 *
 * The server decides a body is falling when its own copy of it is over open floor -- a leap coming down
 * there, or a step off the edge (server/src/campaigns/default/default.ts handleFloorFall) -- and moves it to
 * the storey below with the drop to play. That word reaches the player a round trip after their body went
 * over on their screen, and meanwhile it hung over the edge (a leap) or walked on air (a step), and was then
 * put back where the server had caught it. Both sides make the same call from the same things -- the floor
 * under the soles, a wall's top below it, the storey's rise -- and drop the body the same way, so this client
 * starts the drop itself, in the storey it is still in. When the server's arrives the body is already on its
 * way down, and the server's fall is timed from where this one has got to (floor_fall.ts).
 *
 * A leap picks the drop up on the jump's own curve (leapFallTrajectory) and keeps the speed it took off with;
 * a step drops at rest and stands still, as the server holds it: one storey over FLOOR_FALL_ARRIVAL_MS, or on
 * down the server's chained curve through every open storey below.
 *
 * If the server has the body standing after all, no fall comes; after WAIT_MS the drop is let go and the
 * body is drawn back up where the server has it.
 */

// How long a drop that has come down waits for the server's: a round trip, and then some (MOUNT_WAIT_MS).
const WAIT_MS = 600

const cellOf = (at: { x: number; y: number }) => ({ x: Math.floor(at.x / TileSize), y: Math.floor(at.y / TileSize) })

/** How far down the body would go from where it stands, if it is over open floor: the first storey's rise and all of it. */
const openDrop = (entity: Entity, map: TileMapMetaProvider | undefined):
    { distance: number; total: number; below: TileMapMetaProvider } | undefined => {
    const house = map?.twoStoreyHouse
    // Only against the storey the body is in: just moved to another, the map up is the last one's for a moment.
    if (!map || !house || house.floor <= 0 || !entity.roomId || map.roomId !== entity.roomId || !entity.location) return undefined
    // Without the storey below (it loads a moment after this one), floor held up by its roof reads as open air.
    const below = map.getFloorSupportProvider()
    if (!below) return undefined
    const feet = { x: entity.location.x, y: entity.location.y + BODY_GROUND_OFFSET }
    if (!map.isUnsupportedFloorAtPosition(feet)) return undefined
    // A wall's top below: a player coming down on one stands there (the server floors it).
    if (wallTopOpenAt(below, map, cellOf(feet))) return undefined
    const distance = (house.rise ?? 1) * STOREY_RISE_PX
    // On down through every storey with nothing under the soles, as the server chains the fall.
    let total = distance
    for (let lower = below; (lower.twoStoreyHouse?.floor ?? 0) > 0;) {
        const next = lower.getFloorSupportProvider()
        if (!next || !lower.isUnsupportedFloorAtPosition(feet) || wallTopOpenAt(next, lower, cellOf(feet))) break
        total += (lower.twoStoreyHouse?.rise ?? 1) * STOREY_RISE_PX
        lower = next
    }
    return { distance, total, below }
}

/** The drop this client's own leap goes into at `now`, its landing, or undefined if it comes down on floor. */
export const startLeapFall = (entity: Entity, map: TileMapMetaProvider | undefined, now: number): PredictedFall | undefined => {
    const drop = openDrop(entity, map)
    if (!drop) return undefined
    const { durationMs, trajectory } = leapFallTrajectory(drop.distance, drop.total)
    return {
        view: { id: "predicted-leap-fall", phase: drop.total > drop.distance ? "falling" : "arriving", fromAbove: true,
            leap: true, stairJump: true, distance: drop.distance, durationMs, remainingMs: durationMs, trajectory },
        startedAt: now,
        roomId: entity.roomId,
    }
}

/** The drop this client's own body goes into on stepping over open floor at `now`, or undefined if it stands there. */
export const startWalkOffFall = (entity: Entity, map: TileMapMetaProvider | undefined, now: number): PredictedFall | undefined => {
    const drop = openDrop(entity, map)
    if (!drop) return undefined
    if (drop.total > drop.distance) {
        // On down through more than one storey: the server's chained fall, from rest, its first storey in FLOOR_FALL_MS
        // and every one after on the same curve. It sets the body down clear of any wall only at the last, so there is
        // no glide across to draw from here.
        const gravity = 2 * drop.distance / (FLOOR_FALL_MS * FLOOR_FALL_MS)
        return {
            view: { id: "predicted-walk-off-fall", phase: "falling", fromAbove: true, distance: drop.distance,
                durationMs: FLOOR_FALL_MS, remainingMs: FLOOR_FALL_MS,
                trajectory: { gravity, distanceBefore: 0, endElapsedMs: FLOOR_FALL_MS, totalDistance: drop.total } },
            startedAt: now,
            roomId: entity.roomId,
        }
    }
    // Set down where the server will set it, clear of a wall under the edge; nowhere, and there is no fall.
    const feet = cellOf({ x: entity.location.x, y: entity.location.y + BODY_GROUND_OFFSET })
    const landing = fallLandingNear(drop.below, { ...entity.location, roomId: drop.below.roomId }, !map!.isFloorOpeningAt(feet.x, feet.y))
    if (!landing) return undefined
    return {
        view: { id: "predicted-walk-off-fall", phase: "arriving", fromAbove: true, distance: drop.distance,
            durationMs: FLOOR_FALL_ARRIVAL_MS, remainingMs: FLOOR_FALL_ARRIVAL_MS },
        startedAt: now,
        roomId: entity.roomId,
        landing: { x: landing.x, y: landing.y },
    }
}

/** The fall's own clock at `now`, on the trajectory it shares with the server's (a leap, or a step off several storeys). */
export const predictedFallElapsedMs = (fall: PredictedFall, now: number): number =>
    fall.view.trajectory!.endElapsedMs - fall.view.durationMs + now - fall.startedAt

/** How long the whole drop takes, down every storey it falls through. */
const dropMs = (fall: PredictedFall): number => {
    if (!fall.view.trajectory) return fall.view.durationMs
    const { gravity, endElapsedMs, totalDistance } = fall.view.trajectory
    return Math.sqrt(2 * (totalDistance ?? 0) / gravity) - (endElapsedMs - fall.view.durationMs)
}

/** Recent enough to be the fall the server is about to send: down, and waited for no longer than WAIT_MS. */
export const predictedFallCurrent = (fall: PredictedFall | undefined, now: number): boolean =>
    !!fall && now - fall.startedAt <= dropMs(fall) + WAIT_MS

/** Still in the air: the drop has not yet come down on the floor it falls to. */
export const predictedFallAirborne = (fall: PredictedFall | undefined, now: number): boolean =>
    !!fall && now - fall.startedAt < dropMs(fall)

/** Still being drawn: in the storey it set out from, and not yet given up on. */
export const predictedFallPending = (entity: Entity, now: number): PredictedFall | undefined => {
    const fall = entity.predictedFall
    return fall && entity.roomId === fall.roomId && predictedFallCurrent(fall, now) ? fall : undefined
}

/**
 * Where the body is drawn at `now` from where it stands in the storey it set out from: down by the drop (0 at the
 * edge, the rise once down), and for a step off, across to where the server sets it down below -- the server moves
 * the body there as the drop begins, and this client's own position stays where the step stopped until it does.
 */
export const predictedFallOffset = (fall: PredictedFall, from: { x: number; y: number }, now: number): { x: number; y: number } => {
    if (fall.view.trajectory) return { x: 0, y: fall.view.distance + floorFallOffsetAt(fall.view, predictedFallElapsedMs(fall, now)) }
    // A step off: the server's one-storey arrival (floor_fall.ts arrivalLift), seen from the storey above.
    const progress = Math.max(0, Math.min(1, (now - fall.startedAt) / fall.view.durationMs))
    const across = predictedFallAcross(fall, from, now)
    return { x: across.x, y: fall.view.distance * progress * progress + across.y }
}

/** How far across a step off has the body drawn towards where the server sets it down, at `now` (world px). */
export const predictedFallAcross = (fall: PredictedFall, from: { x: number; y: number }, now: number): { x: number; y: number } => {
    if (!fall.landing || fall.view.trajectory) return { x: 0, y: 0 }
    const across = Math.max(0, Math.min(1, 2 * (now - fall.startedAt) / fall.view.durationMs))
    return { x: (fall.landing.x - from.x) * across, y: (fall.landing.y - from.y) * across }
}
